import clsx from 'clsx';
import Link from 'next/link';
import { ComponentProps, forwardRef, HTMLProps, ReactNode } from 'react';
import { overrideTailwindClasses } from 'tailwind-override';

import { AppLink } from 'types/AppLink';
import { externalLinkMixin } from 'utils/externalLinkMixin';

import Icon, { IconType } from './Icon';

type AppLinkChildren = {
  text: ReactNode;
  href: string;
  ariaLabel?: string;
};

type Props = {
  link?: AppLink | AppLinkChildren;
  underline?: boolean;
  iconPosition?: 'left' | 'right';
  icon?: IconType;
  iconClassName?: string;
} & HTMLProps<HTMLAnchorElement> &
  Omit<ComponentProps<typeof Link>, 'href'>;

export const StyledLink = forwardRef<HTMLAnchorElement, Props>(
  (
    {
      link,
      shallow,
      prefetch,
      as,
      replace,
      scroll,
      passHref,
      locale,
      className,
      underline = true,
      iconPosition = 'right',
      icon,
      iconClassName,
      children,
      ...rest
    },
    ref
  ) => {
    const href = rest.href ?? link?.href;
    const ariaLabel = rest['aria-label'] ?? link?.ariaLabel;
    const classes = overrideTailwindClasses(
      clsx(
        'hover:cursor-pointer inline-flex',
        {
          underline,
        },
        className
      )
    );
    const childElements = (
      <>
        {iconPosition === 'left' && icon && (
          <Icon
            icon={icon}
            fill="text-inherit"
            className={clsx(iconClassName, 'mr-2')}
          />
        )}
        {children ?? link?.text}
        {iconPosition === 'right' && icon && (
          <Icon
            icon={icon}
            fill="text-inherit"
            className={clsx(iconClassName, 'ml-2')}
          />
        )}
      </>
    );
    if (href === undefined || href?.startsWith('#')) {
      return (
        <a
          className={classes}
          ref={ref}
          aria-label={ariaLabel}
          href={href}
          {...rest}
        >
          {childElements}
        </a>
      );
    }
    return (
      <Link
        shallow={shallow}
        href={href}
        prefetch={prefetch}
        as={as}
        replace={replace}
        scroll={scroll}
        passHref={passHref}
        locale={locale}
        legacyBehavior
      >
        <a
          className={classes}
          ref={ref}
          aria-label={ariaLabel}
          {...externalLinkMixin(href)}
          {...rest}
        >
          {childElements}
        </a>
      </Link>
    );
  }
);

StyledLink.displayName = 'StyledLink';
